import { GitBookAgentClientStatus } from '../../Components';

/**
 * UI states for the finding-level CR creation card.
 */
export enum FindingProcessStatus {
    NOT_STARTED = 'NOT_STARTED',
    PROCESSING = 'IN_PROGRESS',
    DONE = 'DONE',
    FAILED = 'FAILED',
}

type SoftFailureConfirmationInput = {
    agentStatus: GitBookAgentClientStatus;
    currentAgentMessageCount: number;
    agentMessageCountAtRunStart: number;
    linkedChangeRequestCount: number;
    linkedChangeRequestsDataUpdatedAt: number;
    isFetchingLinkedChangeRequests: boolean;
    softFailureCheckStartedAt: number | null;
};

type FindingProcessStatusInput = {
    hasLinkedChangeRequests: boolean;
    hasProcessingMessages: boolean;
    isAgentProcessing: boolean;
    isProcessingFinding: boolean;
    isTriggeringChangeRequests: boolean;
};

/**
 * Returns true while the monitor still needs one fresh zero-CR query result before soft-failing.
 */
export function shouldWaitForSoftFailureConfirmation(input: SoftFailureConfirmationInput): boolean {
    const {
        agentStatus,
        currentAgentMessageCount,
        agentMessageCountAtRunStart,
        linkedChangeRequestCount,
        linkedChangeRequestsDataUpdatedAt,
        isFetchingLinkedChangeRequests,
        softFailureCheckStartedAt,
    } = input;

    if (agentStatus !== GitBookAgentClientStatus.IDLE) {
        return false;
    }

    if (linkedChangeRequestCount > 0 || currentAgentMessageCount <= agentMessageCountAtRunStart) {
        return false;
    }

    if (softFailureCheckStartedAt === null) {
        return true;
    }

    return (
        isFetchingLinkedChangeRequests ||
        linkedChangeRequestsDataUpdatedAt < softFailureCheckStartedAt
    );
}

/**
 * Returns the current UI status for a finding's CR creation block.
 */
export function getFindingProcessStatus(input: FindingProcessStatusInput): FindingProcessStatus {
    const {
        hasLinkedChangeRequests,
        hasProcessingMessages,
        isAgentProcessing,
        isProcessingFinding,
        isTriggeringChangeRequests,
    } = input;

    if (hasLinkedChangeRequests) {
        return FindingProcessStatus.DONE;
    }

    const hasSettledWithoutChangeRequests =
        hasProcessingMessages && !isAgentProcessing && !isProcessingFinding;
    if (hasSettledWithoutChangeRequests) {
        return FindingProcessStatus.FAILED;
    }

    if (isTriggeringChangeRequests || isProcessingFinding || isAgentProcessing) {
        return FindingProcessStatus.PROCESSING;
    }

    return FindingProcessStatus.NOT_STARTED;
}
